import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import Cropper from "react-easy-crop";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Alert,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import {
  fetchLeagueById,
  updateLeague,
  uploadLeagueImage,
} from "../../services/apiServices";

// Image Cropper Modal Component
const ImageCropperModal = ({ isOpen, toggle, image, onCropComplete }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  // When modal opens, reset the crop state
  useEffect(() => {
    if (isOpen) {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      setPreviewUrl(null);
    }
  }, [isOpen]);

  // Handle crop complete
  const onCropCompleteCallback = useCallback((_, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
    generatePreview(croppedAreaPixels);
  }, []);

  // Generate a preview from the crop selection
  const generatePreview = useCallback(
    async (croppedAreaPixels) => {
      if (!image || !croppedAreaPixels) return;

      try {
        const croppedImage = await createCropPreview(croppedAreaPixels);
        setPreviewUrl(URL.createObjectURL(croppedImage));
      } catch (error) {
        console.error("Error generating preview:", error);
      }
    },
    [image],
  );

  // Create a crop preview and convert to blob for saving
  const createCropPreview = async (croppedAreaPixels) => {
    try {
      if (!croppedAreaPixels || !image) return null;

      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      // Set canvas size to exactly 128x128px
      canvas.width = 92;
      canvas.height = 92;

      const imageElement = new Image();
      imageElement.crossOrigin = "anonymous";

      return new Promise((resolve, reject) => {
        imageElement.onload = () => {
          // Draw the cropped area to the canvas and resize to 128x128
          ctx.drawImage(
            imageElement,
            croppedAreaPixels.x,
            croppedAreaPixels.y,
            croppedAreaPixels.width,
            croppedAreaPixels.height,
            0,
            0,
            92, // Target width
            92, // Target height
          );

          canvas.toBlob(
            (blob) => {
              if (blob) {
                resolve(blob);
              } else {
                reject(new Error("Failed to create blob"));
              }
            },
            "image/jpeg",
            0.9,
          );
        };

        imageElement.onerror = () => {
          reject(new Error("Failed to load image"));
        };

        imageElement.src = image;
      });
    } catch (error) {
      console.error("Error creating crop preview:", error);
      throw error;
    }
  };

  const handleSaveCrop = async () => {
    if (!croppedAreaPixels) return;

    try {
      setLoading(true);
      const croppedBlob = await createCropPreview(croppedAreaPixels);
      onCropComplete(croppedBlob);
      toggle();
    } catch (error) {
      console.error("Error saving crop:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        Crop Image (Final size: 128x128px)
      </ModalHeader>
      <ModalBody>
        <Row>
          <Col md="8">
            <div
              className="crop-container"
              style={{ height: "400px", position: "relative" }}>
              {image ? (
                <Cropper
                  image={image}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  onCropChange={setCrop}
                  onCropComplete={onCropCompleteCallback}
                  onZoomChange={setZoom}
                  cropShape="rect"
                  showGrid={true}
                />
              ) : (
                <div className="text-center p-4 border rounded">
                  <p>No image loaded. Please select an image first.</p>
                </div>
              )}
            </div>
            <div className="controls mt-3">
              <div className="d-flex align-items-center">
                <span className="me-2">Zoom</span>
                <input
                  type="range"
                  value={zoom}
                  min={1}
                  max={3}
                  step={0.1}
                  aria-labelledby="Zoom"
                  onChange={(e) => setZoom(Number(e.target.value))}
                  className="form-range"
                />
              </div>
              <div className="text-muted mt-2">
                Use the slider to zoom, drag to reposition, then click Apply
                Crop
              </div>
            </div>
          </Col>
          <Col md="4">
            <div className="preview-container mt-3">
              <h6>Preview:</h6>
              {previewUrl ? (
                <div
                  className="square-preview"
                  style={{
                    width: "150px",
                    height: "150px",
                    overflow: "hidden",
                    borderRadius: "4px",
                    border: "1px solid #ced4da",
                  }}>
                  <img
                    src={previewUrl}
                    alt="Crop preview"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </div>
              ) : (
                <div
                  style={{
                    width: "150px",
                    height: "150px",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "4px",
                    border: "1px solid #ced4da",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}>
                  <span className="text-muted">No preview</span>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={loading}>
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={handleSaveCrop}
          disabled={loading || !croppedAreaPixels}>
          {loading ? (
            <>
              <Spinner size="sm" className="me-2" /> Processing...
            </>
          ) : (
            "Apply Crop"
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

// Draggable Image Upload Component
const DraggableImageUpload = ({
  onFileSelect,
  currentImage,
  isUploading,
  uploadError,
}) => {
  const [showCropper, setShowCropper] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(currentImage || null);

  useEffect(() => {
    setPreviewUrl(currentImage);
  }, [currentImage]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(URL.createObjectURL(file));
      setShowCropper(true);
    }
  };

  const handleDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setSelectedFile(URL.createObjectURL(file));
      setShowCropper(true);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    accept: {
      "image/*": [".png", ".jpg", ".jpeg", ".gif"],
    },
    multiple: false,
  });

  const handleCropComplete = (croppedBlob) => {
    const croppedUrl = URL.createObjectURL(croppedBlob);
    setPreviewUrl(croppedUrl);
    onFileSelect(croppedBlob);
    setShowCropper(false);
  };

  const toggleCropper = () => {
    setShowCropper(!showCropper);
  };

  return (
    <>
      <div
        {...getRootProps()}
        className={`dropzone-container p-3 border rounded ${
          isDragActive ? "border-primary" : ""
        }`}
        style={{
          borderStyle: "dashed",
          cursor: "pointer",
          backgroundColor: isDragActive ? "rgba(0, 123, 255, 0.1)" : "#fafafa",
          minHeight: "120px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}>
        <input {...getInputProps()} disabled={isUploading} />

        {previewUrl ? (
          <div className="text-center">
            <img
              src={previewUrl}
              alt="Preview"
              style={{
                maxHeight: "100px",
                maxWidth: "100%",
                marginBottom: "10px",
              }}
              className="img-thumbnail"
            />
            <p className="mb-0">Drag & drop a new image or click to replace</p>
          </div>
        ) : (
          <div className="text-center">
            {isDragActive ? (
              <p className="mb-0">Drop the image here...</p>
            ) : (
              <>
                <i
                  className="ri-upload-cloud-line"
                  style={{ fontSize: "2rem" }}></i>
                <p className="mb-0">
                  Drag & drop an image here, or click to select
                </p>
                <p className="small text-muted">
                  (Image will be cropped to 1:1 ratio)
                </p>
              </>
            )}
          </div>
        )}
      </div>

      {isUploading && (
        <div className="mt-2">
          <Spinner size="sm" /> Uploading...
        </div>
      )}

      {uploadError && (
        <Alert color="danger" className="mt-2" fade={false}>
          {uploadError}
        </Alert>
      )}

      <div className="mt-2">
        <Label className="btn btn-outline-secondary btn-sm">
          Browse Files
          <Input
            type="file"
            style={{ display: "none" }}
            onChange={handleFileChange}
            accept="image/*"
            disabled={isUploading}
          />
        </Label>
      </div>

      <ImageCropperModal
        isOpen={showCropper}
        toggle={toggleCropper}
        image={selectedFile}
        onCropComplete={handleCropComplete}
      />
    </>
  );
};

const EditLeague = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [league, setLeague] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [newImageFile, setNewImageFile] = useState(null);
  const [imageUrl, setImageUrl] = useState("");

  // Fetch league data
  useEffect(() => {
    const getLeague = async () => {
      try {
        setLoading(true);
        const response = await fetchLeagueById(id);
        if (response.success) {
          setLeague(response.league);
          setImageUrl(response.league.image);
        } else {
          setError(response.message || "Failed to fetch league details");
        }
      } catch (error) {
        console.error("Error fetching league:", error);
        if (error.status === 403) {
          setError(
            "You don't have permission to view this league. Please check your admin privileges.",
          );
        } else if (error.status === 401) {
          setError("Your session has expired. Please log in again.");
        } else {
          setError(error.message || "Error fetching league details");
        }
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      getLeague();
    }
  }, [id]);

  // Handle image file selection
  const handleImageSelect = (file) => {
    setNewImageFile(file);
    setUploadError(null);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!league) return;

    // Check if user is authenticated
    const token = localStorage.getItem("authToken");
    if (!token) {
      setError("You are not logged in. Please log in again.");
      return;
    }

    try {
      setUpdating(true);
      setError(null);
      setSuccess(false);

      let finalImageUrl = imageUrl;

      // Upload new image if selected
      if (newImageFile) {
        setUploadingImage(true);
        try {
          finalImageUrl = await uploadLeagueImage(newImageFile);
          setImageUrl(finalImageUrl);
        } catch (uploadErr) {
          setUploadError("Failed to upload image");
          return;
        } finally {
          setUploadingImage(false);
        }
      }

      // Update league with same name, sub_category but new image
      const updateData = {
        name: league.name,
        image: finalImageUrl,
        sub_category: league.sub_category._id,
      };

      const response = await updateLeague(id, updateData);

      if (response.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate("/leagues");
        }, 2000);
      } else {
        setError(response.message || "Failed to update league");
      }
    } catch (error) {
      console.error("Error updating league:", error);
      if (error.status === 403) {
        setError(
          "You don't have permission to update leagues. Please check your admin privileges.",
        );
      } else if (error.status === 401) {
        setError("Your session has expired. Please log in again.");
      } else {
        setError(error.message || "Error updating league");
      }
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center p-5">
            <Spinner color="primary" /> Loading league details...
          </div>
        </Container>
      </div>
    );
  }

  if (error && !league) {
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="mrmto" breadcrumbItem="Edit League" />
          <Alert color="danger" fade={false}>
            {error}
          </Alert>
          <Button color="secondary" onClick={() => navigate("/leagues")}>
            Back to Leagues
          </Button>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="mrmto" breadcrumbItem="Edit League" />

        <Row>
          <Col lg="8">
            <Card>
              <CardBody>
                <Form onSubmit={handleSubmit}>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label>League Name</Label>
                        <Input
                          type="text"
                          value={league?.name || ""}
                          disabled
                          className="bg-light"
                        />
                        <small className="text-muted">
                          League name cannot be modified
                        </small>
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label>Sub Category</Label>
                        <Input
                          type="text"
                          value={league?.sub_category?.name || ""}
                          disabled
                          className="bg-light"
                        />
                        <small className="text-muted">
                          Sub category cannot be modified
                        </small>
                      </FormGroup>
                    </Col>
                  </Row>

                  <FormGroup>
                    <Label>League Image</Label>
                    <DraggableImageUpload
                      onFileSelect={handleImageSelect}
                      currentImage={imageUrl}
                      isUploading={uploadingImage}
                      uploadError={uploadError}
                    />
                  </FormGroup>

                  {error && (
                    <Alert color="danger" className="mb-3" fade={false}>
                      {error}
                    </Alert>
                  )}

                  {success && (
                    <Alert color="success" className="mb-3" fade={false}>
                      League updated successfully! Redirecting...
                    </Alert>
                  )}

                  {uploadError && (
                    <Alert color="danger" className="mb-3" fade={false}>
                      {uploadError}
                    </Alert>
                  )}

                  <div className="d-flex justify-content-between">
                    <Button
                      type="button"
                      color="secondary"
                      onClick={() => navigate("/leagues")}
                      disabled={updating}>
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      color="primary"
                      disabled={updating || uploadingImage}>
                      {updating ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Updating...
                        </>
                      ) : (
                        "Update League"
                      )}
                    </Button>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>

          <Col lg="4">
            <Card>
              <CardBody>
                <h5>Current League Details</h5>
                <div className="mt-3">
                  <div className="mb-3">
                    <strong>Name:</strong> {league?.name}
                  </div>
                  <div className="mb-3">
                    <strong>Sub Category:</strong> {league?.sub_category?.name}
                  </div>
                  <div className="mb-3">
                    <strong>Created:</strong>{" "}
                    {new Date(league?.createdAt).toLocaleDateString()}
                  </div>
                  <div className="mb-3">
                    <strong>Current Image:</strong>
                    <div className="mt-2">
                      {league?.image ? (
                        <img
                          src={league.image}
                          alt={league.name}
                          style={{
                            width: "100px",
                            height: "100px",
                            objectFit: "cover",
                            borderRadius: "4px",
                            border: "1px solid #ced4da",
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: "100px",
                            height: "100px",
                            backgroundColor: "#f0f0f0",
                            borderRadius: "4px",
                            border: "1px solid #ced4da",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}>
                          No Image
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default EditLeague;
